import { atom, useAtom, useStore } from "jotai";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { QaPage, QaPlace, QaTurn } from "@CommonApi/merchflows/_merchflowId_/qa";
import { useTranslation } from "src/i18n";
import { ORateLimitedError, OUnavailableError, postMerchflowQa } from "./api";
import type { CopilotTrouble } from "./useConfigCopilot";

export interface OQaEntry {
  id: number;
  role: QaTurn["role"];
  text: string;
  /** The page the question was asked on: on a question, and on O's answer to it. */
  page?: QaPage;
  /** O's answer: the question it answers, for feedback. */
  question?: string;
  /** Server-issued answer identifier, so feedback joins this answer's event. */
  answerId?: string;
  /** O's answer: a thumbs up or down on it went through. One vote per answer. */
  isVoted?: boolean;
  /** O's answer: a vote on it is on its way, so a reopened drawer cannot send a second. */
  isVoting?: boolean;
  /** O's answer: a vote that met an error, kept here so a reopened drawer still offers its retry. */
  failedVote?: { helpful: boolean; comment?: string };
  goTo?: QaPlace[];
  chips?: string[];
  /** Said by the page, not by O's model: never sent back as history. */
  isLocal?: boolean;
}

export interface OQaThread {
  merchflowId: number | null;
  /** Changes with every new thread, so a reply to an old one is dropped. */
  thread: number;
  entries: OQaEntry[];
  isOpen: boolean;
  isAsking: boolean;
  /** The last question met no model. */
  isUnavailable: boolean;
  /** From O's last answer; null until one lands. */
  running: boolean | null;
  /** A question held after the merchant reaches the current Q&A allowance. */
  retry: { text: string; page: QaPage } | null;
  /** A question that met any other error: kept here so a reopened drawer still offers Try again. */
  failed: { text: string; page: QaPage } | null;
}

const HISTORY_TURNS = 8;
const HISTORY_TEXT_MAX = 2000;
const GO_TO_MAX = 2;
const CHIPS_MAX = 3;

const threadFor = (merchflowId: number | null, thread: number): OQaThread => ({
  merchflowId,
  thread,
  entries: [],
  isOpen: false,
  isAsking: false,
  isUnavailable: false,
  running: null,
  retry: null,
  failed: null,
});

/** One thread for the merchflow open, held above the pages so it outlives a page change. */
export const oQaThreadAtom = atom<OQaThread>(threadFor(null, 0));
export const oQaRateLimitUntilAtom = atom(0);

let nextEntryId = 1;

const historyOf = (entries: readonly OQaEntry[]): QaTurn[] =>
  entries
    .filter((entry) => !entry.isLocal)
    .slice(-HISTORY_TURNS)
    .map(({ role, text }) => ({ role, text: text.slice(0, HISTORY_TEXT_MAX) }));

/** O's merchflow Q&A: the thread for `merchflowId`, and asking from `page`. */
export const useOQa = (merchflowId: number) => {
  const { t } = useTranslation("common");
  const store = useStore();
  const [state, setState] = useAtom(oQaThreadAtom);
  const [rateLimitUntil, setRateLimitUntil] = useAtom(oQaRateLimitUntilAtom);
  const [now, setNow] = useState(Date.now);
  const askMutation = useMutation({
    mutationFn: ({ text, page, history }: { text: string; page: QaPage; history: QaTurn[] }) =>
      postMerchflowQa(merchflowId, { page, text, history }),
  });
  const isOwn = state.merchflowId === merchflowId;
  const thread = isOwn ? state : threadFor(merchflowId, state.thread + 1);
  const rateLimitSeconds = Math.max(0, Math.ceil((rateLimitUntil - now) / 1000));

  useEffect(() => {
    if (rateLimitUntil <= Date.now()) return;
    setNow(Date.now());
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= rateLimitUntil) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [rateLimitUntil]);

  useEffect(() => {
    setState((now) =>
      now.merchflowId === merchflowId ? now : threadFor(merchflowId, now.thread + 1),
    );
  }, [merchflowId]);

  const setOpen = (isOpen: boolean) =>
    setState((now) => (now.merchflowId === merchflowId ? { ...now, isOpen } : now));

  const changeEntry = (
    entryId: number,
    change: Pick<OQaEntry, "isVoted" | "isVoting" | "failedVote">,
  ) =>
    setState((now) =>
      now.merchflowId === merchflowId
        ? {
            ...now,
            entries: now.entries.map((entry) =>
              entry.id === entryId ? { ...entry, ...change } : entry,
            ),
          }
        : now,
    );
  const markVoting = (entryId: number) =>
    changeEntry(entryId, { isVoting: true, failedVote: undefined });
  const markVoted = (entryId: number) => changeEntry(entryId, { isVoted: true, isVoting: false });
  const markVoteFailed = (entryId: number, failedVote: OQaEntry["failedVote"]) =>
    changeEntry(entryId, { isVoting: false, failedVote });

  const ask = async (text: string, page: QaPage): Promise<boolean> => {
    const current = store.get(oQaThreadAtom);
    if (
      current.merchflowId !== merchflowId ||
      current.isAsking ||
      store.get(oQaRateLimitUntilAtom) > Date.now()
    )
      return false;
    const started = current.thread;
    const isCurrent = () => {
      const now = store.get(oQaThreadAtom);
      return now.merchflowId === merchflowId && now.thread === started;
    };
    const history = historyOf(current.entries);
    const said = nextEntryId++;
    const onThread = (change: (now: OQaThread) => OQaThread) =>
      setState((now) =>
        now.merchflowId === merchflowId && now.thread === started ? change(now) : now,
      );
    onThread((now) => ({
      ...now,
      entries: [...now.entries, { id: said, role: "user", text, page }],
      isAsking: true,
      retry: null,
      failed: null,
    }));
    try {
      const response = await askMutation.mutateAsync({ page, text, history });
      onThread((now) => ({
        ...now,
        entries: [
          ...now.entries,
          {
            id: nextEntryId++,
            role: "o",
            text: response.answer,
            page,
            question: text,
            answerId: response.answerId,
            goTo: response.goTo.slice(0, GO_TO_MAX),
            chips: response.chips.slice(0, CHIPS_MAX),
          },
        ],
        isUnavailable: false,
        running: response.running,
      }));
      return true;
    } catch (error) {
      if (error instanceof ORateLimitedError) {
        if (!isCurrent()) return false;
        setRateLimitUntil((until) => Math.max(until, Date.now() + error.retryAfterSeconds * 1000));
        onThread((now) => ({
          ...now,
          entries: now.entries.filter((entry) => entry.id !== said),
          retry: { text, page },
        }));
        return false;
      }
      if (error instanceof OUnavailableError) {
        onThread((now) => ({
          ...now,
          entries: [
            ...now.entries,
            {
              id: nextEntryId++,
              role: "o",
              text: t("configCopilot.qa.unavailable"),
              isLocal: true,
            },
          ],
          isUnavailable: true,
        }));
        return true;
      }
      onThread((now) => ({
        ...now,
        entries: now.entries.filter((entry) => entry.id !== said),
        failed: { text, page },
      }));
      return false;
    } finally {
      onThread((now) => ({ ...now, isAsking: false }));
    }
  };

  const { failed } = thread;
  const trouble: CopilotTrouble | null = failed && {
    line: t("configCopilot.qa.notAnswered"),
    retry: () => void ask(failed.text, failed.page),
  };

  return {
    ...thread,
    trouble,
    rateLimitSeconds,
    ask,
    setOpen,
    markVoting,
    markVoted,
    markVoteFailed,
  };
};
